var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import URLX from './Url';
import * as itemCache from './CurrentPage/cache';
import * as appShell from './AppShell/utils';
import { autoResolveDynamicData } from './DynamicData';
import { clearCacheAndUnregisterServiceWorker } from './service-worker/registration';
import { isIE } from './device-type';
export const LINK_IDENTIFIER_HEADER = 'X-Link-Identifier';
export const REQUEST_ACTION_HEADER = 'X-Request-Action';
export const INCLUDE_APP_SHELL_DATA_HEADER = 'X-IncludeAppShellData';
export const RESOLVE_DYNAMIC_DATA_HEADER = 'X-ResolveDynamicData';
export const VERSION_HEADER_NAME = 'X-Server-Version';
export const REQUIRE_RELOAD_HEADER = 'X-Require-Reload';
export const ADD_TO_CART_ACTION = 'addtocart';
export const CLICK_ACTION = 'click';
let onAuthRequired = null;
/**
 * Use this to implement "seamless auth", meaning that the passed in callback here
 * will be called if the server returns 401. This gives you a chance to login the user
 * and once the promise resolves that the callback returns the original request
 * will be replayed.
 */
export function setOnAuthRequired(callback) {
    onAuthRequired = callback;
}
export function get(url, headers, abortSignal) {
    return load(url, 'get', undefined, headers, true, abortSignal);
}
export function getItem(url, useCache = false, abortSignal) {
    url = typeof url === 'string' ? new URLX(url) : url;
    let cachePromise = null;
    if (useCache) {
        const cacheItem = itemCache.getCacheItem(url);
        if (cacheItem) {
            cachePromise = Promise.resolve(cacheItem);
        }
    }
    // Even if we use the cache we still want to get a fresh version of it
    // and update the cache
    const fetchPromise = getJson(url, undefined, true, abortSignal).then((item) => {
        const cacheItem = item;
        if (itemCache.isCacheItem(cacheItem)) {
            itemCache.setCacheItem(cacheItem);
        }
        return item;
    });
    return cachePromise ? cachePromise : fetchPromise;
}
export function getItems(url, abortSignal) {
    return getJson(url, undefined, true, abortSignal).then((items) => {
        if (items) {
            items.forEach(i => {
                if (itemCache.isCacheItem(i)) {
                    itemCache.setCacheItem(i);
                }
            });
        }
        return items;
    });
}
export function getJson(url, headers, includeDefaultHeaders = true, abortSignal) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield load(url, 'get', undefined, headers, includeDefaultHeaders, abortSignal).then(r => r.json());
        autoResolveDynamicData(result);
        return result;
    });
}
export function post(url, body, headers, includeDefaultHeaders = true, abortSignal) {
    return load(url, 'post', body, headers, includeDefaultHeaders, abortSignal);
}
export function postJson(url, body, headers, includeDefaultHeaders = true, abortSignal) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield load(url, 'post', body, headers, includeDefaultHeaders, abortSignal).then(r => r.json());
        autoResolveDynamicData(result);
        return result;
    });
}
function load(url, method, body, headers = {}, includeDefaultHeaders = true, abortSignal) {
    // We append the extra querystring here because we can't request the same url for Ajax as what's in
    // the browser address bar. If we use the same url, the browser will use the JSON response from the browser
    // cache to serve up to the user if the user goes back from the site and then forward to it.
    url = new URLX(url);
    if (includeDefaultHeaders) {
        // Oddly enough these extra headers causes issues when trying to preload json responses.
        // Any extra headers will invalidate
        headers = Object.assign({
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-Client-Version': CURRENT_VERSION,
        }, headers);
    }
    let promise = fetch(url.hiddenHref, {
        headers,
        credentials: 'same-origin',
        method,
        body: body !== undefined ? JSON.stringify(body) : isIE() ? null : undefined,
        signal: abortSignal,
    })
        .then(r => {
        if (r.headers.has(REQUIRE_RELOAD_HEADER)) {
            window.location.reload();
            // Return never resolving promise to pause the chain so the page can reload
            return new Promise(() => null);
        }
        const isNewMajorVersion = serverHasDifferentMajorVersion(r);
        const isNewVersionAndShouldBeTreatedAsMajor = TREAT_EVERY_RELEASE_AS_MAJOR && serverHasDifferentVersion(r);
        if (isNewMajorVersion || isNewVersionAndShouldBeTreatedAsMajor) {
            if (isNewMajorVersion) {
                console.info('Nuking the local caches because the server has a higher major version');
            }
            if (isNewVersionAndShouldBeTreatedAsMajor) {
                console.info('Nuking the local caches because the treatEveryReleaseAsMajor is set to true in config and server has a higher version');
            }
            return Promise.all([
                clearCacheAndUnregisterServiceWorker(),
                itemCache.nukeCache(),
                appShell.nukeAppShell(),
            ]).then(() => {
                // Might seem drastic to do a full page reload when we get an Ajax response since it
                // will probably interrupt the user. But a major version change means that data structures
                // differ in a way that the client has to update. Major version changes should be few and far
                // between enough to not make this a usability issue.
                window.location.reload();
                // Return never resolving promise to pause the chain so the page can reload
                return new Promise(() => null);
            });
        }
        else {
            return r;
        }
    })
        .then(r => {
        if (r.status >= 200 && r.status < 400) {
            return r;
        }
        else {
            if (r.status === 401 && onAuthRequired) {
                return onAuthRequired(r).then(() => {
                    return load(url, method, body, headers, includeDefaultHeaders);
                }, () => {
                    return Promise.reject({
                        status: r.status,
                        response: r,
                    });
                });
            }
            return Promise.reject({
                status: r.status,
                response: r,
            });
        }
    });
    if (process.env.NODE_ENV !== 'production') {
        const delayNumber = (window.chaosMonkeyMode && window.chaosMonkeyMode.delay) || Math.round(Math.random() * 2 * 100) / 100;
        const failNumber = (window.chaosMonkeyMode && window.chaosMonkeyMode.fail) || 0.5;
        const delay = (value) => {
            console.debug('Delaying request for', delayNumber, 'seconds', url.hiddenHref);
            return new Promise(resolve => setTimeout(() => resolve(value), delayNumber * 1000));
        };
        const randomlyFail = (value) => {
            if (Math.random() < failNumber) {
                console.debug('Simulating failed request, probability: ', failNumber, url.hiddenHref);
                return Promise.reject({
                    status: 500,
                });
            }
            else {
                return value;
            }
        };
        if (window.chaosMonkeyMode) {
            promise = promise.then(delay).then(randomlyFail);
        }
    }
    return promise;
}
function serverHasDifferentVersion(response) {
    const serverVersion = response.headers.get(VERSION_HEADER_NAME);
    return serverVersion ? serverVersion !== CURRENT_VERSION : false;
}
function serverHasDifferentMajorVersion(response) {
    // If the response is an error we might have failed to add the server version
    if (!response.headers.has(VERSION_HEADER_NAME)) {
        if (response.ok || response.status === 503) {
            // If the response is missing the version header but not an error
            // the response has been intercepted by a maintenance page or something
            // like it which indicates that we should clear everything and reload.
            return true;
        }
        console.warn("Response didn't contain a server version in header");
        return false;
    }
    const serverVersion = response.headers.get(VERSION_HEADER_NAME);
    return serverVersion ? majorVersionDiffers(serverVersion, CURRENT_VERSION) : false;
}
let isContinuousDeployEnvironment = false;
export function setIsContinuousDeployEnvironment(value) {
    isContinuousDeployEnvironment = value;
}
export function majorVersionDiffers(version1, version2) {
    const version1Array = (version1 || '').split('.');
    const version2Array = (version2 || '').split('.');
    // For continuous deployment purposes, if you deploy the same version but with
    // differing revision number (e.g. 1.0.0.1234 vs 1.0.0.1235) we consider this
    // to be a major version to prevent projects in early phases have to increase major
    // version all the time.
    if (isContinuousDeployEnvironment &&
        version1Array.length === version2Array.length &&
        version1Array.length === 4 &&
        `${version1Array[0]}.${version1Array[1]}.${version1Array[2]}` ===
            `${version2Array[0]}.${version2Array[1]}.${version2Array[2]}` &&
        version1Array[3] !== version2Array[3]) {
        return true;
    }
    return version1Array[0] !== version2Array[0];
}
